<template>
  <div class="cts-module">
    <!-- Header: Periodo y Métricas KPI -->
    <b-card no-body class="p-1 mb-1 border-0 shadow-sm cts-header-card" :class="isDarkSkin ? 'bg-dark' : 'bg-white'">
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-1">
        <!-- Selector de Periodo y Badges informativos -->
        <div class="d-flex flex-wrap align-items-center gap-2 mb-50 mb-md-0">
          <div class="d-flex align-items-center mr-1">
            <feather-icon icon="CalendarIcon" size="18" class="text-primary mr-50" />
            <span class="font-weight-bolder mr-50">Periodo CTS:</span>
            <b-form-select
              v-if="periodos.length > 0"
              v-model="periodoSeleccionadoId"
              :options="periodos"
              value-field="id"
              text-field="anio_periodo"
              size="sm"
              class="font-weight-bold shadow-none"
              style="min-width: 170px;"
            />
          </div>

          <!-- Información del Periodo Activo -->
          <div v-if="periodoInfo" class="d-flex flex-wrap align-items-center gap-1">
            <b-badge variant="light-info" class="p-50 mr-50 font-weight-bold">
              <feather-icon icon="ClockIcon" size="12" class="mr-25" />
              {{ periodoInfo.fecha_inicio | myGlobal }} al {{ periodoInfo.fecha_fin | myGlobal }}
            </b-badge>
            <b-badge
              :variant="diasAtrasoPeriodo > 0 ? 'light-danger' : 'light-primary'"
              class="p-50 font-weight-bold"
              v-b-tooltip.hover.top="'Fecha límite establecida para el depósito bancario de CTS'"
            >
              <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
              Depósito: {{ periodoInfo.fecha_limite_deposito | myGlobal }}
              <span v-if="diasAtrasoPeriodo > 0" class="ml-25">({{ diasAtrasoPeriodo }}d atraso)</span>
            </b-badge>
          </div>
        </div>

        <!-- Botones de Acción Global -->
        <div class="d-flex align-items-center flex-wrap gap-1">
          <!-- Botón de Aprobación Masiva -->
          <b-button
            v-if="totalSeleccionados > 0"
            variant="success"
            size="sm"
            class="mr-50 font-weight-bolder shadow-sm"
            @click="aprobarCts"
          >
            <feather-icon icon="CheckCircleIcon" size="14" class="mr-25" />
            APROBAR ({{ totalSeleccionados }})
          </b-button>

          <!-- Botón Recalcular Periodo Completo -->
          <b-button
            v-if="items.length > 0"
            variant="outline-primary"
            size="sm"
            class="mr-50 font-weight-bold"
            @click="generarCts"
            v-b-tooltip.hover.top="'Actualiza el cálculo de CTS para todos los colaboradores con los datos más recientes'"
          >
            <feather-icon icon="RefreshCcwIcon" size="14" class="mr-25" />
            RECALCULAR PERIODO
          </b-button>

          <!-- Botón Nuevo Periodo -->
          <b-button
            variant="primary"
            size="sm"
            class="font-weight-bold"
            @click="openModalGenerarCts"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />
            NUEVO PERIODO
          </b-button>
        </div>
      </div>

      <!-- Tarjetas de Resumen KPI -->
      <b-row class="mt-50 mx-0">
        <b-col lg="3" sm="6" cols="12" class="px-50 mb-50 mb-lg-0">
          <div class="kpi-card p-1 rounded d-flex align-items-center" :class="isDarkSkin ? 'bg-secondary-dark' : 'bg-light-secondary'">
            <div class="avatar bg-light-primary mr-1 p-50 rounded">
              <feather-icon icon="UsersIcon" size="20" class="text-primary" />
            </div>
            <div>
              <div class="font-weight-bolder text-xl leading-none">{{ items.length }}</div>
              <small class="text-muted font-weight-bold text-uppercase">Total Colaboradores</small>
            </div>
          </div>
        </b-col>

        <b-col lg="3" sm="6" cols="12" class="px-50 mb-50 mb-lg-0">
          <div class="kpi-card p-1 rounded d-flex align-items-center" :class="isDarkSkin ? 'bg-secondary-dark' : 'bg-light-secondary'">
            <div class="avatar bg-light-warning mr-1 p-50 rounded">
              <feather-icon icon="ClockIcon" size="20" class="text-warning" />
            </div>
            <div>
              <div class="font-weight-bolder text-xl leading-none">{{ totalGenerados }}</div>
              <small class="text-muted font-weight-bold text-uppercase">Por Aprobar (Generados)</small>
            </div>
          </div>
        </b-col>

        <b-col lg="3" sm="6" cols="12" class="px-50 mb-50 mb-lg-0">
          <div class="kpi-card p-1 rounded d-flex align-items-center" :class="isDarkSkin ? 'bg-secondary-dark' : 'bg-light-secondary'">
            <div class="avatar bg-light-success mr-1 p-50 rounded">
              <feather-icon icon="CheckIcon" size="20" class="text-success" />
            </div>
            <div>
              <div class="font-weight-bolder text-xl leading-none">{{ totalAprobados }}</div>
              <small class="text-muted font-weight-bold text-uppercase">Aprobados</small>
            </div>
          </div>
        </b-col>

        <b-col lg="3" sm="6" cols="12" class="px-50">
          <div class="kpi-card p-1 rounded d-flex align-items-center" :class="isDarkSkin ? 'bg-secondary-dark' : 'bg-light-secondary'">
            <div class="avatar bg-light-success mr-1 p-50 rounded">
              <feather-icon icon="DollarSignIcon" size="20" class="text-success" />
            </div>
            <div>
              <div class="font-weight-bolder text-xl leading-none text-success">
                S/ {{ sumaCTSDepositado | currency }}
              </div>
              <small class="text-muted font-weight-bold text-uppercase">Monto Total CTS</small>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Barra de Filtros y Búsqueda -->
    <b-card no-body class="p-1 mb-1 border-0 shadow-sm" :class="isDarkSkin ? 'bg-dark' : 'bg-white'">
      <b-row class="align-items-center mx-0">
        <!-- Input de Búsqueda Rápida -->
        <b-col md="5" cols="12" class="px-0 mb-50 mb-md-0">
          <b-input-group size="sm">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" size="14" />
            </b-input-group-prepend>
            <b-form-input
              v-model="filtroBusqueda"
              placeholder="Buscar por colaborador, DNI, área o régimen..."
              trim
            />
            <b-input-group-append v-if="filtroBusqueda">
              <b-button size="sm" variant="outline-secondary" @click="filtroBusqueda = ''">
                <feather-icon icon="XIcon" size="12" />
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-col>

        <!-- Filtros Rápidos por Estado -->
        <b-col md="7" cols="12" class="px-0 d-flex justify-content-md-end justify-content-start flex-wrap gap-1">
          <b-button-group size="sm">
            <b-button
              :variant="filtroEstado === 'TODOS' ? 'primary' : 'outline-primary'"
              @click="filtroEstado = 'TODOS'"
            >
              Todos ({{ items.length }})
            </b-button>
            <b-button
              :variant="filtroEstado === 'GENERADO' ? 'primary' : 'outline-primary'"
              @click="filtroEstado = 'GENERADO'"
            >
              Por Aprobar ({{ totalGenerados }})
            </b-button>
            <b-button
              :variant="filtroEstado === 'APROBADO' ? 'primary' : 'outline-primary'"
              @click="filtroEstado = 'APROBADO'"
            >
              Aprobados ({{ totalAprobados }})
            </b-button>
            <b-button
              :variant="filtroEstado === 'OBSERVADOS' ? 'primary' : 'outline-primary'"
              @click="filtroEstado = 'OBSERVADOS'"
            >
              Observados ({{ totalObservados }})
            </b-button>
          </b-button-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tabla Principal de CTS -->
    <b-card no-body class="border-0 shadow-sm overflow-hidden" :class="isDarkSkin ? 'bg-dark' : 'bg-white'">
      <div class="table-responsive" style="max-height: 65vh; overflow-y: auto;">
        <b-table-simple
          small
          hover
          class="table-sticky mb-0 custom-cts-table text-nowrap"
          :class="isDarkSkin ? 'table-dark' : ''"
        >
          <b-thead>
            <b-tr>
              <b-th class="sticky-top text-center" style="width: 45px;">
                <b-form-checkbox
                  v-model="allSelected"
                  :indeterminate="haySeleccionParcial"
                  @change="handleSelectAll"
                />
              </b-th>
              <b-th class="sticky-top text-left">COLABORADOR</b-th>
              <b-th class="sticky-top text-center">ESTADO</b-th>
              <b-th class="sticky-top text-center">OBSERVACIÓN</b-th>
              <b-th class="sticky-top text-center">F. INGRESO</b-th>
              <b-th class="sticky-top text-center">TIEMPO COMPUTABLE</b-th>
              <b-th class="sticky-top text-right">SUELDO BASE</b-th>
              <b-th class="sticky-top text-right">ASIG. FAM.</b-th>
              <b-th class="sticky-top text-right">PROM. H.EXT</b-th>
              <b-th class="sticky-top text-right">PROM. BONO</b-th>
              <b-th class="sticky-top text-right">1/6 GRATIF.</b-th>
              <b-th class="sticky-top text-right">REMUN. COMPUTABLE</b-th>
              <b-th class="sticky-top text-right">CTS CALCULADA</b-th>
              <b-th class="sticky-top text-right">INTERÉS</b-th>
              <b-th class="sticky-top text-right text-success font-weight-bolder">CTS A DEPOSITAR</b-th>
              <b-th class="sticky-top text-center" style="min-width: 95px;">ACCIÓN</b-th>
            </b-tr>
          </b-thead>

          <b-tbody v-if="filteredItems.length > 0">
            <b-tr
              v-for="(item, idx) in filteredItems"
              :key="item.id || idx"
              :class="{
                'row-selected': item.selected,
                'row-observado': item.motivos && item.motivos.length > 0
              }"
            >
              <!-- Checkbox de selección -->
              <b-td class="text-center align-middle">
                <b-form-checkbox
                  v-if="item.estado === 'GENERADO'"
                  v-model="item.selected"
                  :value="true"
                  :unchecked-value="false"
                />
                <feather-icon v-else icon="Check" size="14" class="text-success" />
              </b-td>

              <!-- Colaborador -->
              <b-td class="align-middle">
                <div class="d-flex align-items-center">
                  <div class="avatar mr-75" :class="item.tipo_personal === 'Driver' ? 'bg-light-warning' : 'bg-light-primary'">
                    <feather-icon
                      :icon="item.tipo_personal === 'Driver' ? 'TruckIcon' : 'UserIcon'"
                      size="14"
                      :class="item.tipo_personal === 'Driver' ? 'text-warning' : 'text-primary'"
                    />
                  </div>
                  <div>
                    <router-link
                      target="_blank"
                      :class="[textLink, 'font-weight-bolder']"
                      :to="{
                        name: 'rrhh-employees-information',
                        params: { id: item.employee_id },
                      }"
                    >
                      {{ item.empleado | capitalize }}
                    </router-link>
                    <div class="d-flex align-items-center gap-1 font-small-2 text-muted">
                      <span>DNI: {{ item.numero_documento }}</span>
                      <span class="mx-25">•</span>
                      <span>{{ item.area }}</span>
                      <b-badge
                        variant="light-secondary"
                        class="ml-50 px-50 py-25 font-small-1 font-weight-normal"
                      >
                        {{ item.regimen_laboral || 'GENERAL' }}
                      </b-badge>
                    </div>
                  </div>
                </div>
              </b-td>

              <!-- Estado -->
              <b-td class="text-center align-middle">
                <b-badge
                  :variant="
                    item.estado === 'APROBADO'
                      ? 'light-success'
                      : item.estado === 'GENERADO'
                      ? 'light-info'
                      : 'light-danger'
                  "
                  class="font-weight-bold"
                >
                  {{ item.estado }}
                </b-badge>
              </b-td>

              <!-- Observaciones / Motivos -->
              <b-td class="text-center align-middle">
                <template v-if="item.motivos && item.motivos.length > 0">
                  <b-badge
                    variant="light-warning"
                    class="cursor-pointer font-weight-bold px-50 py-25"
                    @click="mostrarMotivos(item)"
                    v-b-tooltip.hover.top="item.motivos[0].mensaje"
                  >
                    <feather-icon icon="AlertTriangleIcon" size="12" class="mr-25" />
                    {{ item.motivos[0].tipo === 'error' ? 'Requiere Ajuste' : 'Aviso' }}
                  </b-badge>
                </template>
                <template v-else>
                  <span class="text-muted font-small-2">—</span>
                </template>
              </b-td>

              <!-- Fecha de Ingreso -->
              <b-td class="text-center align-middle font-small-2">
                {{ item.fecha_ingreso ? (item.fecha_ingreso | myGlobal) : '—' }}
              </b-td>

              <!-- Tiempo Computable -->
              <b-td class="text-center align-middle font-small-2">
                <span class="font-weight-bold">
                  {{ item.meses_computables || 0 }}m {{ item.dias_computables || 0 }}d
                </span>
                <span v-if="item.dias_inasistencia > 0" class="text-danger ml-25" v-b-tooltip.hover.top="`${item.dias_inasistencia} inasistencia(s) descontada(s)`">
                  (-{{ item.dias_inasistencia }}d)
                </span>
              </b-td>

              <!-- Sueldo Base -->
              <b-td class="text-right align-middle font-weight-bold">
                S/ {{ item.sueldo_basico | currency }}
              </b-td>

              <!-- Asignación Familiar -->
              <b-td class="text-right align-middle text-muted">
                S/ {{ item.asignacion_familiar | currency }}
              </b-td>

              <!-- Promedio Horas Extras -->
              <b-td class="text-right align-middle text-muted">
                S/ {{ item.promedio_horas_extras | currency }}
              </b-td>

              <!-- Promedio Bonos -->
              <b-td class="text-right align-middle text-muted">
                S/ {{ item.promedio_bono_desempeno | currency }}
              </b-td>

              <!-- 1/6 Gratificación -->
              <b-td class="text-right align-middle text-muted">
                S/ {{ item.promedio_gratificacion | currency }}
              </b-td>

              <!-- Remuneración Computable -->
              <b-td class="text-right align-middle font-weight-bolder text-primary">
                S/ {{ item.remuneracion_base_cts | currency }}
              </b-td>

              <!-- CTS Calculada -->
              <b-td class="text-right align-middle">
                S/ {{ item.cts_calculado | currency }}
              </b-td>

              <!-- Interés Legal -->
              <b-td class="text-right align-middle">
                <span :class="Number(item.interes_cts) > 0 ? 'text-warning font-weight-bold' : 'text-muted'">
                  S/ {{ item.interes_cts | currency }}
                </span>
              </b-td>

              <!-- Total CTS a Depositar -->
              <b-td class="text-right align-middle text-success font-weight-bolder" style="font-size: 1.05rem;">
                S/ {{ item.cts_dep_en | currency }}
              </b-td>

              <!-- Acciones Individuales -->
              <b-td class="text-center align-middle" style="white-space: nowrap;">
                <b-button
                  variant="flat-primary"
                  size="sm"
                  class="btn-icon p-25 mr-25"
                  @click="abrirModalDetalle(item)"
                  v-b-tooltip.hover.top="item.estado === 'GENERADO' ? 'Ver cálculo detallado y aprobar' : 'Ver detalle de liquidación'"
                >
                  <feather-icon icon="EyeIcon" size="15" />
                </b-button>
                <b-button
                  v-if="item.estado === 'GENERADO'"
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon p-25"
                  @click="recalcularItem(item)"
                  v-b-tooltip.hover.top="'Recalcular datos para este colaborador'"
                >
                  <feather-icon icon="RotateCcwIcon" size="14" />
                </b-button>
                <feather-icon v-else icon="CheckCircleIcon" size="15" class="text-success" />
              </b-td>
            </b-tr>
          </b-tbody>

          <!-- Estado Vacío -->
          <b-tbody v-else>
            <b-tr>
              <b-td colspan="16" class="text-center py-3 text-muted">
                <feather-icon icon="InboxIcon" size="32" class="mb-50 text-muted" /><br />
                <span class="font-weight-bold">No se encontraron registros de CTS para el periodo o criterio seleccionado.</span>
              </b-td>
            </b-tr>
          </b-tbody>

          <!-- Fila de Totales (Sticky Footer) -->
          <b-tfoot v-if="filteredItems.length > 0">
            <b-tr class="sticky-footer font-weight-bolder" :class="isDarkSkin ? 'bg-dark text-white' : 'bg-light text-dark'">
              <b-td colspan="6" class="text-left align-middle pl-1 text-uppercase">
                TOTAL GENERAL ({{ filteredItems.length }} registros)
              </b-td>
              <b-td class="text-right align-middle">S/ {{ totalSueldoBasico | currency }}</b-td>
              <b-td class="text-right align-middle">S/ {{ totalAsignacionFamiliar | currency }}</b-td>
              <b-td class="text-right align-middle">S/ {{ totalHorasExtras | currency }}</b-td>
              <b-td class="text-right align-middle">S/ {{ totalBonos | currency }}</b-td>
              <b-td class="text-right align-middle">S/ {{ totalGratificacion | currency }}</b-td>
              <b-td class="text-right align-middle text-primary">S/ {{ totalBaseComputable | currency }}</b-td>
              <b-td class="text-right align-middle">S/ {{ totalCtsCalculado | currency }}</b-td>
              <b-td class="text-right align-middle text-warning">S/ {{ totalInteres | currency }}</b-td>
              <b-td class="text-right align-middle text-success" style="font-size: 1.1rem;">
                S/ {{ totalCtsDepositado | currency }}
              </b-td>
              <b-td></b-td>
            </b-tr>
          </b-tfoot>
        </b-table-simple>
      </div>
    </b-card>

    <!-- Modal para Crear Nuevo Periodo de CTS -->
    <ModalGenerarCts
      v-if="showModalGenerar"
      :show="showModalGenerar"
      @close="showModalGenerar = false"
      @generated="handleGenerated"
    />

    <!-- Modal de Observaciones / Motivos del Colaborador -->
    <b-modal
      v-model="modalMotivosVisible"
      title="Observaciones del Cálculo de CTS"
      ok-only
      ok-title="Entendido"
      modal-class="modal-warning"
      centered
    >
      <div v-if="colaboradorSeleccionado">
        <h6 class="font-weight-bolder mb-25">{{ colaboradorSeleccionado.empleado }}</h6>
        <p class="text-muted font-small-2 mb-1">
          DNI: {{ colaboradorSeleccionado.numero_documento }} | Área: {{ colaboradorSeleccionado.area }}
        </p>

        <b-alert
          v-for="(motivo, i) in colaboradorSeleccionado.motivos"
          :key="i"
          show
          :variant="motivo.tipo === 'error' ? 'danger' : 'warning'"
          class="p-1 mb-1 font-small-2"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              :icon="motivo.tipo === 'error' ? 'AlertOctagonIcon' : 'AlertTriangleIcon'"
              size="16"
              class="mr-50 mt-25 flex-shrink-0"
            />
            <div>
              <span class="font-weight-bold text-uppercase">{{ motivo.tipo }}:</span>
              {{ motivo.mensaje }}
            </div>
          </div>
        </b-alert>
      </div>
    </b-modal>

    <!-- Modal Detallado e Intuitivo de Cálculo y Aprobación de CTS -->
    <ModalDetalleCts
      v-if="modalDetalleVisible && colaboradorDetalle"
      :visible="modalDetalleVisible"
      :colaborador="colaboradorDetalle"
      :periodo-info="periodoInfo"
      :periodo-seleccionado="periodoSeleccionado"
      :dias-atraso="diasAtrasoPeriodo"
      @close="cerrarModalDetalle"
      @aprobado="handleDetalleAprobado"
      @recalcular="handleDetalleRecalcular"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CtsService from "@/views/rrhh/views/payroll/components/cts/service/cts.service.js";
import ModalGenerarCts from "@/views/rrhh/views/payroll/components/cts/components/ModalGenerarCts.vue";
import ModalDetalleCts from "@/views/rrhh/views/payroll/components/cts/components/ModalDetalleCts.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment";

export default {
  components: {
    ModalGenerarCts,
    ModalDetalleCts,
  },
  data() {
    return {
      periodos: [],
      periodoSeleccionadoId: null,
      periodoInfo: null,
      ultimosMeses: [],
      items: [],
      allSelected: false,
      filtroBusqueda: "",
      filtroEstado: "TODOS",
      showModalGenerar: false,
      modalMotivosVisible: false,
      colaboradorSeleccionado: null,
      modalDetalleVisible: false,
      colaboradorDetalle: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    periodoSeleccionado() {
      return this.periodos.find((p) => p.id === this.periodoSeleccionadoId) || null;
    },
    diasAtrasoPeriodo() {
      if (!this.periodoInfo?.fecha_limite_deposito) return 0;
      const limite = moment(this.periodoInfo.fecha_limite_deposito);
      const hoy = moment().startOf("day");
      return hoy.isAfter(limite) ? hoy.diff(limite, "days") : 0;
    },
    filteredItems() {
      let list = this.items;

      // Filtro por Estado
      if (this.filtroEstado === "GENERADO") {
        list = list.filter((item) => item.estado === "GENERADO");
      } else if (this.filtroEstado === "APROBADO") {
        list = list.filter((item) => item.estado === "APROBADO");
      } else if (this.filtroEstado === "OBSERVADOS") {
        list = list.filter((item) => item.motivos && item.motivos.length > 0);
      }

      // Filtro por Búsqueda de texto
      if (this.filtroBusqueda.trim()) {
        const q = this.filtroBusqueda.toLowerCase().trim();
        list = list.filter(
          (item) =>
            (item.empleado && item.empleado.toLowerCase().includes(q)) ||
            (item.numero_documento && item.numero_documento.includes(q)) ||
            (item.area && item.area.toLowerCase().includes(q)) ||
            (item.regimen_laboral && item.regimen_laboral.toLowerCase().includes(q)) ||
            (item.estado && item.estado.toLowerCase().includes(q))
        );
      }

      return list;
    },
    totalSeleccionados() {
      return this.items.filter((item) => item.selected).length;
    },
    totalGenerados() {
      return this.items.filter((item) => item.estado === "GENERADO").length;
    },
    totalAprobados() {
      return this.items.filter((item) => item.estado === "APROBADO").length;
    },
    totalObservados() {
      return this.items.filter((item) => item.motivos && item.motivos.length > 0).length;
    },
    haySeleccionParcial() {
      const generados = this.items.filter((item) => item.estado === "GENERADO");
      const seleccionados = generados.filter((item) => item.selected);
      return seleccionados.length > 0 && seleccionados.length < generados.length;
    },
    // Sumatorias calculadas sobre los registros filtrados
    totalSueldoBasico() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.sueldo_basico) || 0), 0);
    },
    totalAsignacionFamiliar() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.asignacion_familiar) || 0), 0);
    },
    totalHorasExtras() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.promedio_horas_extras) || 0), 0);
    },
    totalBonos() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.promedio_bono_desempeno) || 0), 0);
    },
    totalGratificacion() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.promedio_gratificacion) || 0), 0);
    },
    totalBaseComputable() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.remuneracion_base_cts) || 0), 0);
    },
    totalCtsCalculado() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.cts_calculado) || 0), 0);
    },
    totalInteres() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.interes_cts) || 0), 0);
    },
    totalCtsDepositado() {
      return this.filteredItems.reduce((acc, item) => acc + (Number(item.cts_dep_en) || 0), 0);
    },
  },
  watch: {
    periodoSeleccionadoId(newVal) {
      if (newVal) {
        this.myProvider();
      }
    },
  },
  async mounted() {
    await this.getPeriodos();
  },
  methods: {
    async getPeriodos() {
      try {
        const { data } = await CtsService.getPeriodos();
        this.periodos = data || [];
        if (this.periodos.length > 0) {
          this.periodoSeleccionadoId = this.periodos[0].id;
        } else {
          this.periodoSeleccionadoId = null;
        }
      } catch (error) {
        console.error("Error al cargar periodos de CTS:", error);
        this.periodos = [];
      }
    },
    async myProvider() {
      if (!this.periodoSeleccionadoId) {
        this.items = [];
        return;
      }

      try {
        this.addPreloader();
        const params = {
          periodo_id: this.periodoSeleccionadoId,
        };
        const res = await CtsService.index(params);
        const data = res.data || {};

        // Soporta tanto array directo (nuevo backend) como paginador
        const lista = Array.isArray(data.data) ? data.data : (data.data?.data || []);

        this.items = lista.map((row) => ({
          ...row,
          selected: false,
        }));
        this.allSelected = false;
        this.ultimosMeses = data.ultimosMeses || [];
        this.periodoInfo = data.periodo || null;
      } catch (error) {
        console.error("Error al cargar datos de CTS:", error);
        this.showToastError(error?.response?.data?.message || "No se pudo cargar la información de CTS.");
        this.items = [];
      } finally {
        this.removePreloader();
      }
    },
    handleSelectAll() {
      const nuevoEstado = this.allSelected;
      this.items.forEach((item) => {
        if (item.estado === "GENERADO") {
          item.selected = nuevoEstado;
        }
      });
    },
    handleGenerated() {
      this.showModalGenerar = false;
      this.getPeriodos();
    },
    openModalGenerarCts() {
      this.showModalGenerar = true;
    },
    mostrarMotivos(item) {
      this.colaboradorSeleccionado = item;
      this.modalMotivosVisible = true;
    },
    abrirModalDetalle(item) {
      this.colaboradorDetalle = item;
      this.modalDetalleVisible = true;
    },
    cerrarModalDetalle() {
      this.modalDetalleVisible = false;
      this.colaboradorDetalle = null;
    },
    async handleDetalleAprobado() {
      await this.myProvider();
    },
    async handleDetalleRecalcular(colaborador) {
      await this.myProvider();
      const updated = this.items.find((it) => it.id === colaborador.id);
      if (updated) {
        this.colaboradorDetalle = updated;
      }
    },
    async recalcularItem(item) {
      const confirm = await this.showConfirmSwal(
        `¿Recalcular CTS para ${item.empleado}?`,
        "Se actualizarán sus días computables, gratificación e ingresos con la información activa.",
        {
          confirmButtonText: "Sí, recalcular",
          cancelButtonText: "Cancelar",
        }
      );
      if (!confirm.isConfirmed) return;

      try {
        this.addPreloader();
        const { data } = await CtsService.generar({
          periodo: item.periodo || this.periodoSeleccionado?.periodo,
          anio: item.anio || this.periodoSeleccionado?.anio,
          id: item.id,
        });

        this.showToastSuccess(data?.msg || data?.message || "Cálculo actualizado correctamente.");
        await this.myProvider();
      } catch (error) {
        console.error(error);
        this.showToastError(error?.response?.data?.message || error?.message || "Error al recalcular CTS.");
      } finally {
        this.removePreloader();
      }
    },
    async generarCts() {
      if (!this.periodoSeleccionado) return;

      try {
        this.addPreloader();
        // Verificar si aplica interés por depósito extemporáneo
        const { data: dataCts } = await CtsService.preGenerar({
          periodo: this.periodoSeleccionado.periodo,
          anio: this.periodoSeleccionado.anio,
        });

        if (dataCts?.aplica_interes) {
          const mensajeHTML = `
            <div style="text-align: left; font-size: 0.95rem;">
              <p>El depósito para <b>${dataCts.periodo} ${dataCts.anio}</b> venció el <b>${dataCts.fecha_pago}</b>.</p>
              <p>Tiene <b>${dataCts.dias_atraso} días de atraso</b> y se aplicará interés legal laboral con factor diario <b>${dataCts.factor_diario}</b>.</p>
              <p class="mb-0">¿Deseas recalcular todo el periodo?</p>
            </div>
          `;

          const { isConfirmed } = await this.showConfirmSwal(
            "Cálculo de Interés CTS",
            mensajeHTML,
            {
              html: mensajeHTML,
              confirmButtonText: "Sí, recalcular periodo",
              cancelButtonText: "Volver",
              customClass: {
                confirmButton: "btn btn-outline-success mr-1",
                cancelButton: "btn btn-outline-secondary",
              },
            }
          );
          if (!isConfirmed) return;
        } else {
          const { isConfirmed } = await this.showConfirmSwal(
            `¿Recalcular periodo ${this.periodoSeleccionado.anio_periodo}?`,
            "Se actualizarán todos los cálculos de los colaboradores con la versión vigente.",
            {
              confirmButtonText: "Sí, recalcular",
              cancelButtonText: "Cancelar",
            }
          );
          if (!isConfirmed) return;
        }

        this.addPreloader();
        const { data } = await CtsService.generar({
          periodo: this.periodoSeleccionado.periodo,
          anio: this.periodoSeleccionado.anio,
        });

        this.showToastSuccess(data?.msg || data?.message || "Periodo recalculado con éxito.");
        await this.myProvider();
      } catch (error) {
        console.error(error);
        this.showToastError(error?.response?.data?.message || error?.message || "Error al generar CTS.");
      } finally {
        this.removePreloader();
      }
    },
    async aprobarCts() {
      const seleccionados = this.items.filter((item) => item.selected && item.estado === "GENERADO");
      if (seleccionados.length === 0) {
        this.showToastWarning("Selecciona al menos un registro en estado GENERADO para aprobar.");
        return;
      }

      const confirm = await this.showConfirmSwal(
        `¿Desea aprobar ${seleccionados.length} CTS seleccionada(s)?`,
        "Se generará la caja en Tesorería y se cambiará el estado a APROBADO. Esta acción no se puede deshacer.",
        {
          confirmButtonText: "Sí, aprobar y crear cajas",
          cancelButtonText: "Cancelar",
          customClass: {
            confirmButton: "btn btn-outline-success mr-1",
            cancelButton: "btn btn-outline-secondary",
          },
        }
      );
      if (!confirm.isConfirmed) return;

      try {
        this.addPreloader();
        // Payload estricto compatible con CtsController@aprobar de Kevin
        const payload = {
          cts: seleccionados.map((item) => ({ cts_id: item.id })),
          user_id: this.currentUser?.id,
        };

        const { data } = await CtsService.aprobar(payload);

        this.showToastSuccess(data?.msg || data?.message || "CTS encoladas y aprobadas correctamente.");
        await this.myProvider();
      } catch (error) {
        console.error(error);
        this.showToastError(error?.response?.data?.msg || error?.response?.data?.message || "Error al aprobar CTS.");
      } finally {
        this.removePreloader();
      }
    },
  },
};
</script>

<style lang="scss">
.cts-module {
  .cts-header-card {
    transition: all 0.3s ease;
  }

  .kpi-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }
  }

  .custom-cts-table {
    font-size: 0.88rem;

    th {
      font-weight: 700;
      letter-spacing: 0.3px;
      padding: 0.75rem 0.6rem !important;
      border-bottom: 2px solid rgba(0, 0, 0, 0.1) !important;
    }

    td {
      padding: 0.6rem 0.6rem !important;
      vertical-align: middle !important;
    }

    th.sticky-top {
      position: sticky !important;
      top: 0 !important;
      z-index: 10 !important;
      background: #f8f9fa !important;
    }

    tr.row-selected {
      background-color: rgba(115, 103, 240, 0.07) !important;
    }

    tr.row-observado {
      border-left: 3px solid #ff9f43 !important;
    }
  }

  .sticky-footer {
    position: sticky;
    bottom: 0;
    z-index: 9;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.05);
  }

  .bg-secondary-dark {
    background-color: #283046 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;
  }

  .gap-1 {
    gap: 0.5rem;
  }

  .gap-2 {
    gap: 1rem;
  }
}

.dark-layout {
  .cts-module {
    .custom-cts-table th.sticky-top {
      background: #283046 !important;
      color: #d0d2d6 !important;
      border-bottom-color: rgba(255, 255, 255, 0.1) !important;
    }

    .kpi-card {
      border-color: rgba(255, 255, 255, 0.05);
    }

    .sticky-footer {
      background: #161d31 !important;
      color: #fff !important;
    }
  }
}
</style>
